import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { 
  Share2, 
  Download, 
  MessageCircle, 
  Mail, 
  Copy, 
  Check,
  Loader2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

/**
 * PDFShareButton - Componente para baixar e compartilhar PDFs
 * 
 * Usa a Web Share API nativa do navegador quando disponível,
 * permitindo compartilhar via WhatsApp, Email, e outros apps instalados.
 * 
 * Props:
 * - endpoint: URL do endpoint que retorna o PDF (ex: `/trips/${tripId}/cost-summary/pdf`)
 * - filename: Nome do arquivo para download (ex: 'custos.pdf')
 * - title: Título para compartilhamento (ex: 'Resumo de Custos - Viagem')
 * - description: Descrição para compartilhamento
 * - buttonText: Texto do botão (default: 'Compartilhar')
 * - buttonVariant: Variante do botão (default: 'outline')
 * - buttonSize: Tamanho do botão (default: 'default')
 * - className: Classes adicionais
 */
export default function PDFShareButton({
  endpoint,
  filename = 'documento.pdf',
  title = 'Documento',
  description = '',
  buttonText = 'Compartilhar',
  buttonVariant = 'outline',
  buttonSize = 'default',
  className = '',
  showIcon = true,
  disabled = false
}) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if Web Share API is available
  const canShare = navigator.share !== undefined;
  const canShareFiles = navigator.canShare !== undefined;

  // Download PDF and return blob
  const downloadPdfBlob = async () => {
    const response = await api.get(endpoint, {
      responseType: 'blob'
    });
    return new Blob([response.data], { type: 'application/pdf' });
  };

  // Handle direct download
  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await downloadPdfBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      if (error.response?.status === 403) {
        toast.error('Você não tem permissão para baixar este PDF');
      } else {
        toast.error('Erro ao baixar PDF');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle native share (Web Share API)
  const handleNativeShare = async () => {
    setLoading(true);
    try {
      const blob = await downloadPdfBlob();
      const file = new File([blob], filename, { type: 'application/pdf' });
      
      // Check if we can share files
      if (canShareFiles && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: title,
          text: description,
          files: [file]
        });
        toast.success('Compartilhado com sucesso!');
      } else if (canShare) {
        // Fallback: share without file (just title and text)
        // First download the file, then share a message
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        await navigator.share({
          title: title,
          text: `${description}\n\n📎 O PDF foi baixado para seu dispositivo.`
        });
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled share
        return;
      }
      console.error('Error sharing:', error);
      toast.error('Erro ao compartilhar');
    } finally {
      setLoading(false);
    }
  };

  // Handle WhatsApp share (fallback using wa.me)
  const handleWhatsAppShare = async () => {
    setLoading(true);
    try {
      // Download the PDF first
      const blob = await downloadPdfBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Open WhatsApp with message
      const message = encodeURIComponent(
        `${title}\n\n${description}\n\n📎 Estou enviando o PDF "${filename}" em anexo.`
      );
      window.open(`https://wa.me/?text=${message}`, '_blank');
      
      toast.success('PDF baixado! Anexe no WhatsApp.');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao preparar compartilhamento');
    } finally {
      setLoading(false);
    }
  };

  // Handle Email share
  const handleEmailShare = async () => {
    setLoading(true);
    try {
      // Download the PDF first
      const blob = await downloadPdfBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Open email client
      const subject = encodeURIComponent(title);
      const body = encodeURIComponent(
        `${description}\n\n📎 O PDF "${filename}" está em anexo.\n\n--\nEnviado via ViajeJunto`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      
      toast.success('PDF baixado! Anexe no email.');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao preparar email');
    } finally {
      setLoading(false);
    }
  };

  // Handle copy link (if there's a public URL)
  const handleCopyMessage = async () => {
    const text = `${title}\n\n${description}`;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Texto copiado!');
    await new Promise(resolve => setTimeout(resolve, 2000));
    setCopied(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={buttonVariant} 
          size={buttonSize}
          disabled={disabled || loading}
          className={className}
          data-testid="pdf-share-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : showIcon ? (
            <Share2 className="w-4 h-4 mr-2" />
          ) : null}
          {buttonText}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Native Share (mobile) */}
        {canShare && (
          <DropdownMenuItem onClick={handleNativeShare} data-testid="share-native">
            <Share2 className="w-4 h-4 mr-2" />
            Compartilhar...
          </DropdownMenuItem>
        )}
        
        {canShare && <DropdownMenuSeparator />}
        
        {/* WhatsApp */}
        <DropdownMenuItem onClick={handleWhatsAppShare} data-testid="share-whatsapp">
          <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
          WhatsApp
        </DropdownMenuItem>
        
        {/* Email */}
        <DropdownMenuItem onClick={handleEmailShare} data-testid="share-email">
          <Mail className="w-4 h-4 mr-2 text-blue-500" />
          Email
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Download */}
        <DropdownMenuItem onClick={handleDownload} data-testid="share-download">
          <Download className="w-4 h-4 mr-2" />
          Baixar PDF
        </DropdownMenuItem>
        
        {/* Copy text */}
        <DropdownMenuItem onClick={handleCopyMessage} data-testid="share-copy">
          {copied ? (
            <Check className="w-4 h-4 mr-2 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 mr-2" />
          )}
          Copiar texto
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
